/**
 * EpisodeOfCarePage Component
 * 
 * Main page for viewing and managing all oncology episodes of care.
 * Provides a facility-wide view of all episodes with search and filtering.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  Search as SearchIcon,
  Timeline as TimelineIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  LocalHospital as HospitalIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useUserData } from '../../hooks/useUserData';
import { getFacilityEpisodes } from '../../services/episodeOfCareService';
import { PageHeader, PageContainer, ContentCard, HeaderButton } from './oncologyStyles';
import EpisodeOfCareTimeline from './EpisodeOfCareTimeline';

const statusConfig = {
  'Active': { color: 'success' },
  'On Hold': { color: 'warning' },
  'Completed': { color: 'info' },
  'Cancelled': { color: 'error' }
};

const phaseConfig = {
  'Diagnosis': { color: 'default' },
  'Active Treatment': { color: 'primary' },
  'Surveillance': { color: 'info' },
  'Survivorship': { color: 'success' },
  'Palliative': { color: 'warning' },
  'End of Life': { color: 'error' }
};

const EpisodeOfCarePage = () => {
  const navigate = useNavigate();
  const { episodeId } = useParams();
  const { defaultFacility } = useUserData();
  const facilityId = defaultFacility?.id;

  // State
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [phaseFilter, setPhaseFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedEpisode, setSelectedEpisode] = useState(null);
  const [viewMode, setViewMode] = useState(episodeId ? 'timeline' : 'list'); // 'list' or 'timeline'

  const ITEMS_PER_PAGE = 20;

  // Fetch episodes
  const fetchEpisodes = useCallback(async () => {
    if (!facilityId) return;

    try {
      setLoading(true);
      setError(null);

      const result = await getFacilityEpisodes(facilityId, {
        status: statusFilter || undefined,
        currentPhase: phaseFilter || undefined,
        search: searchTerm || undefined,
        page,
        limit: ITEMS_PER_PAGE
      });

      setEpisodes(result.data || []);
      setTotalPages(Math.ceil((result.total || 0) / ITEMS_PER_PAGE));
    } catch (err) {
      console.error('Error fetching episodes:', err);
      setError('Failed to load episodes of care');
    } finally {
      setLoading(false);
    }
  }, [facilityId, statusFilter, phaseFilter, searchTerm, page]);

  useEffect(() => {
    if (viewMode === 'list') {
      fetchEpisodes();
    }
  }, [fetchEpisodes, viewMode]);

  // Handle viewing an episode timeline
  const handleViewTimeline = (episode) => {
    setSelectedEpisode(episode);
    setViewMode('timeline');
    navigate(`/oncology/episodes/${episode.id}`, { replace: true });
  };

  // Handle going back to list
  const handleBackToList = () => {
    setSelectedEpisode(null);
    setViewMode('list');
    navigate('/oncology/episodes', { replace: true });
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return dateString;
    }
  };

  // If viewing timeline
  if (viewMode === 'timeline' && (selectedEpisode || episodeId)) {
    return (
      <PageContainer>
        <Container maxWidth="lg" sx={{ py: 3 }}>
          <EpisodeOfCareTimeline
            episodeId={episodeId || selectedEpisode?.id}
            episode={selectedEpisode}
            onBack={handleBackToList}
            onEventClick={(event) => {
              // Navigate to related record based on event type
              if (event.type === 'encounter' && event.data?.encounterId) {
                navigate(`/encounters/${event.data.encounterId}`);
              } else if (event.type === 'treatment_plan' && event.data?.treatmentPlanId) {
                navigate(`/oncology/treatment-plan/view/${event.data.treatmentPlanId}`);
              } else if (event.type === 'chemo_cycle' && event.data?.cycleId) {
                // Navigate to chemotherapy tracker
              } else if (event.type === 'toxicity' && event.data?.toxicityId) {
                navigate(`/oncology/toxicity/${event.data.toxicityId}`);
              }
            }}
          />
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* Header */}
        <PageHeader
          title="Episodes of Care"
          subtitle="Track oncology patient journeys from diagnosis through treatment"
          icon={<TimelineIcon />}
          backTo="/oncology"
          actions={
            <HeaderButton
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={fetchEpisodes}
              disabled={loading}
            >
              Refresh
            </HeaderButton>
          }
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Filters */}
        <ContentCard sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by patient name, MRN, or episode name..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(1);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPage(1);
                    }}
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="On Hold">On Hold</MenuItem>
                    <MenuItem value="Completed">Completed</MenuItem>
                    <MenuItem value="Cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Phase</InputLabel>
                  <Select
                    value={phaseFilter}
                    label="Phase"
                    onChange={(e) => {
                      setPhaseFilter(e.target.value);
                      setPage(1);
                    }}
                  >
                    <MenuItem value="">All Phases</MenuItem>
                    <MenuItem value="Diagnosis">Diagnosis</MenuItem>
                    <MenuItem value="Active Treatment">Active Treatment</MenuItem>
                    <MenuItem value="Surveillance">Surveillance</MenuItem>
                    <MenuItem value="Survivorship">Survivorship</MenuItem>
                    <MenuItem value="Palliative">Palliative</MenuItem>
                    <MenuItem value="End of Life">End of Life</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Typography variant="body2" color="text.secondary">
                  {episodes.length} episode{episodes.length !== 1 ? 's' : ''}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </ContentCard>

        {/* Episodes Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Patient</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Episode</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Diagnosis</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Phase</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Start Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Encounters</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {episodes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No episodes found. Episodes are automatically created when a cancer diagnosis is recorded.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : episodes.map((episode) => (
                    <TableRow 
                      key={episode.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleViewTimeline(episode)}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {episode.patientName || 'Unknown'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            MRN: {episode.patientMRN || 'N/A'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {episode.episodeName || 'Unnamed Episode'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {episode.diagnosisSummary || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={episode.currentPhase || 'Unknown'}
                          color={phaseConfig[episode.currentPhase]?.color || 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={episode.status || 'Unknown'}
                          color={statusConfig[episode.status]?.color || 'default'}
                        />
                      </TableCell>
                      <TableCell>{formatDate(episode.startDate)}</TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={episode.encounterIds?.length || 0}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Timeline">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewTimeline(episode);
                            }}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(e, value) => setPage(value)}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Container>
    </PageContainer>
  );
};

export default EpisodeOfCarePage;
